import React, { useState } from "react";
import { Button, Input, Radio, Typography, message, Upload, Popconfirm, Grid, Spin } from "antd";
import Icon, { FileTextOutlined, UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUserOnboarding } from "../../../../Context/UserOnboardingContext";
import userOnboardingService from "../../../../Service/user-onboarding.service";
import profileService from "../../../../Service/profile.service";
import AuthService from "../../../../Service/auth.service";
import Paragraph from "antd/es/typography/Paragraph";
import { classifyError } from "../../../../Utils/errorClassifier";
import WizardErrorPanel from "./WizardErrorPanel";

const { Title } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPE = "application/pdf";

const ScreeningSessionWizard: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const {
    jobDescription,
    setJobDescription,
    resumeFile,
    setResumeFile,
    currentStep,
    setCurrentStep,
    setJobPostId,
    jobApplicationId,
    setJobApplicationId,
    setErrorMessage,
    errorMessage,
    errorCategory,
    setErrorCategory,
    failedStep,
    setFailedStep,
  } = useUserOnboarding();

  // Read session data directly from sessionStorage — context values (screeningSessionId,
  // jobDescription) are populated via useEffect in the parent and may not be ready on
  // first render. sessionStorage is synchronous and always up to date.
  const sessionId = sessionStorage.getItem("pending_screening_session_id");
  const storedResumeFileName = sessionStorage.getItem("screening_resume_file_name");
  // Seed the textarea from sessionStorage if context hasn't been hydrated yet
  const initialJobDesc = jobDescription || sessionStorage.getItem("screening_job_description") || "";

  const [wizardStep, setWizardStep] = useState<0 | 1>(0);
  const [useExistingResume, setUseExistingResume] = useState(true);
  const createdJobPostIdRef = React.useRef<string | null>(null);


  /**
   * Step 0 "Proceed": generate + immediately create the job post in DB,
   * then advance to the resume confirmation step.
   */
  const handleProceed = async () => {
    const desc = jobDescription || sessionStorage.getItem("screening_job_description") || "";
    if (!desc.trim()) return;
    console.log("[ScreeningSessionWizard] handleProceed — generateAndCreateJobPost START");
    setCurrentStep("job-post-processing");
    setErrorCategory(null);
    setFailedStep(null);
    try {
      const jobPostId = await userOnboardingService.generateAndCreateJobPost(desc);
      setJobPostId(jobPostId);
      createdJobPostIdRef.current = jobPostId;
      sessionStorage.setItem("onboarding_created_job_post_id", jobPostId);
      console.log("[ScreeningSessionWizard] handleProceed ✓ jobPostId:", jobPostId);
      setCurrentStep("resume-confirm");
      setWizardStep(1);
    } catch (err: any) {
      console.error("[ScreeningSessionWizard] handleProceed ✗", err);
      const classified = classifyError(err);
      setErrorCategory(classified.category);
      setFailedStep('job-post-generation');
      setErrorMessage(err?.message || "Failed to generate job post. Please try again.");
      setCurrentStep("error");
    }
  };

  /**
   * Step 1 "Complete & Continue": upload/process the resume, run profile
   * matching, and optionally trigger AI assessment (validate + qualified).
   */
  const handleComplete = async () => {
    let resolvedJobPostId = createdJobPostIdRef.current
      || sessionStorage.getItem("onboarding_created_job_post_id");
    console.log("[ScreeningSessionWizard] handleComplete — START | jobPostId:", resolvedJobPostId);

    if (!resolvedJobPostId) {
      setErrorMessage("Job post was not created. Please try again.");
      setErrorCategory(null);
      setFailedStep(null);
      setCurrentStep("error");
      return;
    }

    setErrorCategory(null);
    setFailedStep(null);

    try {
      let jobApplicationId: string | undefined;
      setCurrentStep("profile-matching");

      if (!useExistingResume && resumeFile) {
        // User chose a new file — presigned upload then explicit match call
        console.log("[ScreeningSessionWizard] Step 2a: uploadResumeToJobPost | file:", resumeFile.name);
        jobApplicationId = await userOnboardingService.uploadResumeToJobPost(resolvedJobPostId, resumeFile);
        console.log("[ScreeningSessionWizard] Step 2a ✓ jobApplicationId:", jobApplicationId);
        if (jobApplicationId) {
          setJobApplicationId(jobApplicationId);
          console.log("[ScreeningSessionWizard] Step 2a: matchResume...");
          await profileService.matchResume(jobApplicationId, resumeFile);
          console.log("[ScreeningSessionWizard] Step 2a ✓ matchResume done");
        }
      } else {
        // Use the resume already in S3 from the screening session
        const existingFileName = storedResumeFileName;
        if (existingFileName) {
          console.log("[ScreeningSessionWizard] Step 2b: processExistingSessionResume | fileName:", existingFileName);
          jobApplicationId = await userOnboardingService.processExistingSessionResume(
            resolvedJobPostId,
            existingFileName,
            sessionId ?? undefined
          );
          console.log("[ScreeningSessionWizard] Step 2b ✓ jobApplicationId:", jobApplicationId);
          if (jobApplicationId) setJobApplicationId(jobApplicationId);
        } else {
          console.warn("[ScreeningSessionWizard] Step 2b: SKIPPED — no stored resume file name");
        }
      }

      console.log("[ScreeningSessionWizard] handleComplete — COMPLETED ✓");
      setCurrentStep("completed");
    } catch (err: any) {
      console.error("[ScreeningSessionWizard] handleComplete ✗ FAILED", err);
      const classified = classifyError(err);
      setErrorCategory(classified.category);
      // Determine which step failed
      if (!useExistingResume && resumeFile) {
        setFailedStep('resume-upload');
      } else {
        setFailedStep('profile-matching');
      }
      setErrorMessage(err?.message || "Failed to process your request. Please try again.");
      setCurrentStep("error");
    }
  };


  function cancel(e: any) {
    console.log(e);
  }

  const handleSkip = () => {
    const user = AuthService.getInstance().getCurrentUser();
    if (user?._id) localStorage.setItem(`onboarding_completed_${user._id}`, "true");
    navigate("/");
  };

  const handleNonPdfUpload = () => {
    message.warning({ content: "Only PDF files are accepted.", duration: 4 });
  };

  // Navigation handler for completed profile matching
  const handleNavigateAfterCompletion = () => {
    const user = AuthService.getInstance().getCurrentUser();
    if (user?._id) localStorage.setItem(`onboarding_completed_${user._id}`, "true");
    const jobPostId = createdJobPostIdRef.current;
    if (jobApplicationId && jobPostId) {
      navigate(
        `/application-details?applicationId=${jobApplicationId}&jobId=${jobPostId}&showMatchResult=1`,
        { replace: true }
      );
    }
  };

  const isProcessing = currentStep === "job-post-processing" || currentStep === "profile-matching" || currentStep === "completed";
  const isError = currentStep === "error";

  // ── Step 0: JD review ──────────────────────────────────────────────────────
  if (wizardStep === 0) {
    return (
      <div className="uo-processing-console">
        <div className="uo-processing-console__header">
          <Title level={3}>Setting up your job post</Title>
          <Typography.Paragraph type="secondary">Review and confirm your job description before we begin processing.</Typography.Paragraph>
        </div>

        <div
          className="uo-processing-console__body"
          style={{
            flexDirection: screens.md !== false ? "row" : "column",
          }}
        >
          <div className="uo-processing-console__terminal">
            <div style={{ color: "#4b5563", marginBottom: 12, fontSize: 14 }}>
              ■ <span className="term-system">Job description review...</span>
            </div>

            <div style={{ marginTop: 16 }}>
              {!isProcessing && (
                <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 6, fontFamily: "inherit" }}>
                  // Edit your job description below if needed:
                </div>
              )}
              <TextArea
                value={initialJobDesc}
                onChange={(e) => setJobDescription(e.target.value)}
                disabled={isProcessing || isError}
                rows={15}
                placeholder="Job description loaded from your screening session..."
                className={currentStep === "job-post-processing" ? "uo-textarea-scanning" : ""}
                style={{
                  borderRadius: 4,
                  fontSize: 14,
                  resize: "vertical",
                }}
              />
            </div>
          </div>
          <div className="uo-processing-console__right-panel">
            <div className="processing-center">
              <div className="connecting-line left-line" />
              <div className="processing-orb">
                <img src="/icons/ai-stars.svg" alt="AI" />
              </div>
              <div className="connecting-line right-line" />
            </div>

            {isError && errorCategory && failedStep ? (
              <WizardErrorPanel
                classified={{ category: errorCategory, isRetryable: false, requiresUserAction: false }}
                failedStep={failedStep}
                onRetry={handleProceed}
                onSkip={() => navigate("/")}
                onGoToDashboard={() => navigate("/")}
                isRecruiter={false}
              />
            ) : (
              <>
                <div className="uo-processing-console__status">
                  {isProcessing ? (
                    <>
                      <Typography.Title level={5}>Processing Job Post</Typography.Title>
                      <Typography.Paragraph type="secondary">
                        Our AI is generating your structured job post...
                      </Typography.Paragraph>
                    </>
                  ) : (
                    <>
                      <Typography.Title level={5}>Ready to Process</Typography.Title>
                      <Typography.Paragraph>
                        Your job description has been pre-filled from the screening
                        session. Edit if needed, then click Proceed to generate your
                        job post and start AI matching.
                      </Typography.Paragraph>
                    </>
                  )}
                </div>

                <div className="uo-processing-console__actions">
                  {isProcessing ? (
                    <>
                      {currentStep === "job-post-processing" && (
                        <div style={{ textAlign: "center", width: "100%" }}>
                          <Spin />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <Button
                        block
                        type="primary"
                        disabled={!initialJobDesc.trim()}
                        onClick={handleProceed}
                        size="large"
                      >
                        Proceed →
                      </Button>
                      <Popconfirm
                        onConfirm={handleSkip}
                        onCancel={cancel}
                        title="Are you sure you want to skip?"
                        description={<p>Skipping will exit the assessment creation process for the candidate, <br /> However you will be able to create a job post and process candidates later from the console. </p>}
                        icon={<Icon type="question-circle-o" style={{ color: 'red' }} />}
                      >
                        <Button type="text" block size="large">
                          Skip for now
                        </Button>
                      </Popconfirm>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <Typography.Paragraph type="secondary" className="uo-processing-console__footer">
          Once you proceed, our AI will generate a structured job post and
          match it against your resume.
        </Typography.Paragraph>
      </div>
    );
  }

  // ── Step 1: Resume confirmation ────────────────────────────────────────────
  return (
    <div className="uo-processing-console">
      <div className="uo-processing-console__header">
        <Title level={3}>Analyze Candidate Profile </Title>
        <Typography.Paragraph type="secondary">Upload candidate resume to analyze </Typography.Paragraph>
      </div>

      <div
        className="uo-processing-console__body"
        style={{
          flexDirection: screens.md !== false ? "row" : "column",
        }}
      >
        <div className="uo-processing-console__terminal">
          <div style={{ color: "#4b5563", marginBottom: 12, fontSize: 12 }}>
            ■ <span className="term-system">Profile review...</span>
          </div>

          <div style={{ marginTop: 16 }}>
            <TextArea
              value={initialJobDesc}
              disabled={true}
              rows={15}
              placeholder="Job description from screening session..."
              style={{
                borderRadius: 4,
                fontSize: 14,
                resize: "vertical",
                backgroundColor: "#f5f5f5",
              }}
            />
          </div>
        </div>

        <div className="uo-processing-console__right-panel">
          <div className="processing-center" style={{ marginBottom: 48, marginTop: 48 }}>
            <div className="connecting-line left-line" />
            <div className="processing-orb">
              <img src="/icons/ai-stars.svg" alt="AI" />
            </div>
            <div className="connecting-line right-line" />
          </div>

          {isError && errorCategory && failedStep ? (
            <WizardErrorPanel
              classified={{ category: errorCategory, isRetryable: false, requiresUserAction: false }}
              failedStep={failedStep}
              onRetry={() => handleComplete()}
              onUploadDifferentResume={() => {
                setCurrentStep("resume-confirm");
                setUseExistingResume(false);
                setResumeFile(null);
                setErrorCategory(null);
                setFailedStep(null);
              }}
              onSkip={() => navigate("/")}
              onGoToDashboard={() => navigate("/")}
              isRecruiter={false}
            />
          ) : (
            <div className="uo-processing-console__status">
              {currentStep === "profile-matching" ? (
                <>
                  <Typography.Title level={5}>Analyzing Profile</Typography.Title>
                  <Typography.Paragraph type="secondary">
                    Our AI agent analyzes and semantically matches candidate profiles.
                  </Typography.Paragraph>
                </>
              ) : currentStep === "completed" ? (
                <>
                  <Typography.Title level={5}>Profile Analysis Complete</Typography.Title>
                  <Typography.Paragraph type="secondary">
                    Analysis complete! Review the results and proceed.
                  </Typography.Paragraph>
                </>
              ) : (
                <>
                  <Typography.Title level={5}>Confirm Candidate Resume</Typography.Title>
                  <Typography.Paragraph type="secondary">Choose which resume to use for profile analysis.</Typography.Paragraph>
                </>
              )}
            </div>
          )}

          {!(currentStep === "profile-matching" || currentStep === "completed" || isError) && (
            <Radio.Group
              value={useExistingResume ? "existing" : "new"}
              onChange={(e) => {
                const isExisting = e.target.value === "existing";
                setUseExistingResume(isExisting);
                if (isExisting) setResumeFile(null);
              }}
              style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16, width: "100%" }}
            >
              <Radio value="existing">
                <div className="uo-wizard__resume-info">
                  <FileTextOutlined style={{ fontSize: 18 }} />
                  <div>
                    <Title level={5} ellipsis style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Previously uploaded resume</Title>
                    <Paragraph ellipsis style={{ color: "#666", fontSize: 12, margin: 0 }}>{storedResumeFileName}</Paragraph>
                  </div>
                </div>
              </Radio>
              <Radio value="new">
                <div className="uo-wizard__resume-info">
                  <Upload
                    accept=".pdf"
                    maxCount={1}
                    beforeUpload={(file) => {
                      if (file.type !== ALLOWED_FILE_TYPE) {
                        handleNonPdfUpload();
                        return Upload.LIST_IGNORE;
                      }
                      if (file.size > MAX_FILE_SIZE) {
                        message.error("Resume must not exceed 5MB");
                        return Upload.LIST_IGNORE;
                      }
                      setResumeFile(file);
                      return false;
                    }}
                    onRemove={() => setResumeFile(null)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <UploadOutlined style={{ fontSize: 18 }} />
                      <div>
                        <Title level={5} ellipsis style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Upload different resume</Title>
                        <Paragraph style={{ color: "#666", fontSize: 12, margin: 0 }}>PDF only · max 5 MB</Paragraph>
                      </div>
                    </div>
                  </Upload>
                </div>
              </Radio>
            </Radio.Group>
          )}

          {!isError && (
            <div className="uo-processing-console__actions">
              {currentStep === "profile-matching" || currentStep === "completed" ? (
                <>
                  {currentStep === "completed" && (
                    <Button
                      block
                      type="primary"
                      onClick={handleNavigateAfterCompletion}
                      size="large"
                    >
                      Continue →
                    </Button>
                  )}
                  {currentStep === "profile-matching" && (
                    <div style={{ textAlign: "center", width: "100%" }}>
                      <Spin />
                    </div>
                  )}
                </>
              ) : (
                <Button
                  type="primary"
                  onClick={handleComplete}
                  disabled={!useExistingResume && !resumeFile}
                  block
                  size="large"
                >
                  Process Candidate →
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreeningSessionWizard;
